import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Chip, Divider, Pagination, Typography, alpha, useTheme } from '@mui/material'
import EventIcon from '@mui/icons-material/Event'
import { toast } from 'react-toastify'
import DashboardServices from 'services/DashboardServices'
import DashboardContainer from 'components/DashboardContainer'

const INTERNAL_DEADLINE_ROLES = [
    'Subcommittee Secretary',
    'Subcommittee Member',
    'Committee Secretary',
    'Committee Member',
    'Subcommittee Chair'
]

const normalizeRole = (role = '') => String(role).trim().replace(/\s+/g, ' ').toLowerCase()

const shouldUseInternalDeadlines = (userRoles = []) => {
    const normalizedAllowedRoles = INTERNAL_DEADLINE_ROLES.map(normalizeRole)
    const normalizedUserRoles = userRoles.map((role) => {
        // Handle both string and object formats
        if (typeof role === 'string') {
            return normalizeRole(role)
        }
        return normalizeRole(role?.name || role)
    })

    return normalizedUserRoles.some((role) => normalizedAllowedRoles.includes(role))
}

const getDeadlineType = (userRoles = []) => {
    return shouldUseInternalDeadlines(userRoles) ? 'internal' : 'codex'
}

const formatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A'

    try {
        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) return 'N/A'

        const month = date.toLocaleString('en-US', { month: 'short' })
        const day = String(date.getDate()).padStart(2, '0')
        return { month, day }
    } catch {
        return 'N/A'
    }
}

const getDeadlineStatus = (dateString) => {
    if (!dateString) return { label: 'No Date', color: '#9ca3af', bgColor: '#f3f4f6' }

    try {
        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) return { label: 'Invalid Date', color: '#9ca3af', bgColor: '#f3f4f6' }

        const now = new Date()
        const startOfNow = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const startOfDeadline = new Date(date.getFullYear(), date.getMonth(), date.getDate())
        const diffInDays = Math.floor((startOfNow.getTime() - startOfDeadline.getTime()) / (1000 * 60 * 60 * 24))

        if (diffInDays > 0) {
            return { label: 'Overdue', color: '#dc2626', bgColor: '#fee2e2' }
        } else if (diffInDays === 0) {
            return { label: 'Today', color: '#f97316', bgColor: '#fed7aa' }
        } else {
            return { label: 'On schedule', color: '#16a34a', bgColor: '#dcfce7' }
        }
    } catch {
        return { label: 'Unknown', color: '#9ca3af', bgColor: '#f3f4f6' }
    }
}

const getOneWeekDateRange = () => {
    const now = new Date()
    const year = now.getUTCFullYear()
    const month = String(now.getUTCMonth() + 1).padStart(2, '0')
    const day = String(now.getUTCDate()).padStart(2, '0')

    const oneWeekLater = new Date(now)
    oneWeekLater.setUTCDate(oneWeekLater.getUTCDate() + 7)
    const laterYear = oneWeekLater.getUTCFullYear()
    const laterMonth = String(oneWeekLater.getUTCMonth() + 1).padStart(2, '0')
    const laterDay = String(oneWeekLater.getUTCDate()).padStart(2, '0')

    return {
        from: `${year}-${month}-${day}`,
        to: `${laterYear}-${laterMonth}-${laterDay}`,
    }
}

const UpcomingDeadlines = ({ userRoles = [], role = '' }) => {
    const theme = useTheme()
    const [deadlines, setDeadlines] = useState([])
    const [loading, setLoading] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize] = useState(4)
    const [totalPages, setTotalPages] = useState(1)
    const [totalItems, setTotalItems] = useState(0)

    const deadlineType = useMemo(() => {
        const type = getDeadlineType(userRoles)
        return type
    }, [userRoles])

    const dateRange = useMemo(() => getOneWeekDateRange(), [])

    const requestParams = useMemo(() => {
        const params = { deadline_type: deadlineType, page: currentPage, limit: pageSize }

        if (role) {
            params.role = role
        }

        if (deadlineType === 'internal') {
            params.internal_deadline_from = dateRange.from
            params.internal_deadline_to = dateRange.to
        } else {
            params.codex_deadline_from = dateRange.from
            params.codex_deadline_to = dateRange.to
        }

        return params
    }, [deadlineType, dateRange, currentPage, pageSize, role])

    const getData = useCallback(async () => {
        setLoading(true)
        try {
            const response = await DashboardServices.getUpcomingDeadlines(requestParams)

            if (response.success) {
                const pagination = response?.data?.data?.pagination || response?.data?.pagination || null
                const deadlinesList = Array.isArray(response?.data?.data?.data)
                    ? response.data.data.data
                    : Array.isArray(response?.data?.data)
                        ? response.data.data
                        : Array.isArray(response?.data)
                            ? response.data
                            : []

                const backendPage = Number(pagination?.page)
                const backendLimit = Number(pagination?.limit)
                const backendTotal = Number(pagination?.total)
                const backendTotalPages = Number(pagination?.total_pages)

                setCurrentPage(Number.isNaN(backendPage) ? currentPage : backendPage)
                setTotalItems(Number.isNaN(backendTotal) ? deadlinesList.length : backendTotal)

                if (!Number.isNaN(backendTotalPages) && backendTotalPages > 0) {
                    setTotalPages(backendTotalPages)
                } else if (!Number.isNaN(backendTotal) && !Number.isNaN(backendLimit) && backendLimit > 0) {
                    setTotalPages(Math.max(1, Math.ceil(backendTotal / backendLimit)))
                } else {
                    setTotalPages(1)
                }

                setDeadlines(deadlinesList)
            } else {
                setDeadlines([])
                setTotalItems(0)
                setTotalPages(1)
                toast.error(response.message || 'Failed to load upcoming deadlines.')
            }
        } catch (error) {
            console.error('Error loading upcoming deadlines:', error)
            setDeadlines([])
            setTotalItems(0)
            setTotalPages(1)
            toast.error('Error loading deadlines.')
        } finally {
            setLoading(false)
        }
    }, [requestParams, currentPage])

    useEffect(() => {
        setCurrentPage(1)
    }, [deadlineType])

    useEffect(() => {
        getData()
    }, [getData])

    return (
        <DashboardContainer
            title="Upcoming Deadlines"
            action={
                <EventIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
            }
        >
            {loading && (
                <Typography sx={{ py: 3, color: theme.palette.text.secondary, textAlign: 'center' }}>
                    Loading upcoming deadlines...
                </Typography>
            )}

            {!loading && deadlines.length === 0 && (
                <Typography sx={{ py: 3, color: theme.palette.text.secondary, textAlign: 'center' }}>
                    No upcoming deadlines for the next week.
                </Typography>
            )}

            {!loading && deadlines.length > 0 && (
                <>
                    <Box sx={{ maxHeight: { xs: 400, md: 500 }, overflowY: 'auto', pr: 1 }}>
                        {deadlines.map((item, index) => {
                            const deadlineField = deadlineType === 'internal' ? 'internal_deadline' : 'codex_deadline'
                            const overdueField = deadlineType === 'internal' ? 'is_internal_overdue' : 'is_codex_overdue'
                            const deadline = item?.[deadlineField]
                            const isOverdue = item?.[overdueField]
                            const dateDisplay = formatDateDisplay(deadline)
                            const displayTitle = item?.title || 'Untitled'

                            let statusInfo
                            if (isOverdue === true) {
                                statusInfo = { label: 'Overdue', color: '#dc2626', bgColor: '#fee2e2' }
                            } else if (isOverdue === false) {
                                // Check if it's today
                                if (deadline) {
                                    try {
                                        const date = new Date(deadline)
                                        const now = new Date()
                                        const startOfNow = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                                        const startOfDeadline = new Date(date.getFullYear(), date.getMonth(), date.getDate())
                                        const diffInDays = Math.floor((startOfNow.getTime() - startOfDeadline.getTime()) / (1000 * 60 * 60 * 24))

                                        if (diffInDays === 0) {
                                            statusInfo = { label: 'Today', color: '#f97316', bgColor: '#fed7aa' }
                                        } else {
                                            statusInfo = { label: 'On schedule', color: '#16a34a', bgColor: '#dcfce7' }
                                        }
                                    } catch {
                                        statusInfo = { label: 'On schedule', color: '#16a34a', bgColor: '#dcfce7' }
                                    }
                                } else {
                                    statusInfo = { label: 'On schedule', color: '#16a34a', bgColor: '#dcfce7' }
                                }
                            } else {
                                statusInfo = { label: 'Unknown', color: '#9ca3af', bgColor: '#f3f4f6' }
                            }

                            return (
                                <Box key={item?.id || `${displayTitle}-${index}`}>
                                    <Box
                                        sx={{
                                            py: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0, flex: 1 }}>
                                            <Box
                                            
                                                sx={{
                                                    backgroundColor: alpha('#e90000', 0.07),
                                                    minWidth: 56,
                                                    minHeight: 40,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    textAlign: 'center',
                                                    borderRadius: 3,
                                                }}
                                            >
                                                {typeof dateDisplay === 'object' ? (
                                                    <>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '1.1rem',
                                                                fontWeight: 700,
                                                                color: theme.palette.error.main,
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            {dateDisplay.day}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                fontWeight: 600,
                                                                color: theme.palette.text.secondary,
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            {dateDisplay.month}
                                                        </Typography>

                                                    </>
                                                ) : (
                                                    <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                                                        {dateDisplay}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.75rem', md: '0.9rem' },
                                                        color: theme.palette.text.primary,
                                                        mb: 0.5,
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {displayTitle}
                                                </Typography>

                                                <Chip
                                                    label={statusInfo.label}
                                                    size="small"
                                                    sx={{
                                                        height: 22,
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                        color: statusInfo.color,
                                                        backgroundColor: statusInfo.bgColor,
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                backgroundColor: alpha(theme.palette.text.primary, 0.08),
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.error.main, 0.12),
                                                },
                                                color: theme.palette.error.main,
                                                fontSize: '1.2rem',
                                                lineHeight: 1,
                                            }}
                                        >
                                            ⊘
                                        </Box>
                                    </Box>

                                    {index < deadlines.length - 1 && <Divider />}
                                </Box>
                            )
                        })}
                    </Box>

                    {totalPages > 1 && (
                        <Box
                            sx={{
                                mt: 2,
                                pt: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                                Showing page {currentPage} of {totalPages} ({totalItems} items)
                            </Typography>

                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(_, page) => setCurrentPage(page)}
                                size="small"
                                color="primary"
                                shape="rounded"
                                siblingCount={0}
                                boundaryCount={1}
                            />
                        </Box>
                    )}
                </>
            )}
        </DashboardContainer>
    )
}

export default UpcomingDeadlines
